// src/app/services/launch-darkly.service.ts
import { Injectable } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { environment } from '../../../environments/environment';
import { RootScopeService } from './root-scope.service';

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
  constructor(private rootScopeService: RootScopeService) {}

  private ldClient: LDClient.LDClient | null = null;
  private ldSdkKey = environment.launch_darkly_sdk_key;
  private loading?: Promise<void>;
  private loadedUserId?: string;
  private loadGeneration = 0;
  private unsubscribers: (() => void)[] = [];

  /** One entry per flag: adding a flag means adding a line here. */
  private readonly flagSetters: Record<string, (value: boolean) => void> = {
    'failed-uploads-flag': value => this.rootScopeService.setFailedUploadsFlag(value),
    'db-admin-flag': value => this.rootScopeService.setDbAdminFlag(value),
  };

  /**
   * Initialize the LaunchDarkly client with a provided context. If no context is passed,
   * a default anonymous user context is used. Await this before calling getFlag / onFlagChange.
   */
  async initialize(context?: LDClient.LDContext): Promise<void> {
    const ldContext: LDClient.LDContext = context ?? {
      kind: 'user',
      key: this.rootScopeService.globals.currentUserId || 'anonymous-temp-key',

    };

    this.closeClient();
    this.ldClient = LDClient.initialize(this.ldSdkKey, ldContext);
    await this.ldClient.waitForInitialization(5);
    console.log('LaunchDarkly initialized with context:', ldContext);
  }

  /**
   * Starts LaunchDarkly for the current user and publishes every flag to RootScopeService.
   * Safe to call from both login and app startup: repeat calls for the same user share one load,
   * and change handlers are registered once per client. While a load is in flight "ready" is false
   * and the flags are off, so guards wait. If LaunchDarkly cannot be reached, all flags stay off
   * but "ready" is still set so route guards deny immediately instead of timing out.
   */
  loadFlags(): Promise<void> {
    const userId = this.rootScopeService.globals.currentUserId;
    if (this.loading && this.loadedUserId === userId) {
      return this.loading;
    }
    this.loadedUserId = userId;
    const generation = ++this.loadGeneration;
    this.loading = this.startClient(userId, generation);
    return this.loading;
  }

  private async startClient(userId: string | undefined, generation: number): Promise<void> {
    this.unsubscribers.forEach(unsubscribe => unsubscribe());
    this.unsubscribers = [];
    this.rootScopeService.setLaunchDarklyReady(false);
    this.setAllFlags(false);
    try {
      await this.initialize(userId ? { kind: 'user', key: userId } : undefined);
      if (generation !== this.loadGeneration) return; // a newer load owns the client and the state
      Object.entries(this.flagSetters).forEach(([flagKey, setFlag]) => {
        setFlag(this.getFlag(flagKey, false));
        this.unsubscribers.push(this.onFlagChange<boolean>(flagKey, current => setFlag(current)));
      });
    } catch (e) {
      if (generation !== this.loadGeneration) return;
      console.error('LaunchDarkly failed to load, leaving flags off', e);
      this.closeClient();
      this.setAllFlags(false);
      this.loading = undefined; // allow a later call to retry
      this.loadedUserId = undefined;
    }
    this.rootScopeService.setLaunchDarklyReady(true);
  }

  private setAllFlags(value: boolean): void {
    Object.values(this.flagSetters).forEach(setFlag => setFlag(value));
  }

  private closeClient(): void {
    const client = this.ldClient;
    this.ldClient = null;
    client?.close().catch(() => {});
  }

  getFlag<T extends LDClient.LDFlagValue>(flagKey: string, defaultValue: T): T {
    if (!this.ldClient) {
      return defaultValue;
    }
    return this.ldClient.variation(flagKey, defaultValue as LDClient.LDFlagValue) as T;
  }

  onFlagChange<T extends LDClient.LDFlagValue>(
    flagKey: string,
    callback: (current: T, previous: T) => void
  ): () => void {
    if (!this.ldClient) {
      // No client yet; return a noop unsubscriber
      return () => {};
    }
    const handler = (current: LDClient.LDFlagValue, previous: LDClient.LDFlagValue) => {
      callback(current as T, previous as T);
    };
    this.ldClient.on(`change:${flagKey}`, handler);
    // Return unsubscribe
    return () => {
      this.ldClient?.off(`change:${flagKey}`, handler);
    };
  }

}
